import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import {
  faBolt,
  faBox,
  faBoxOpen,
  faArrowLeft,
  faBuilding,
  faCalendarDay,
  faCheckCircle,
  faCircleInfo,
  faClock,
  faCopy,
  faCreditCard,
  faDollarSign,
  faEye,
  faFileInvoiceDollar,
  faIdBadge,
  faInbox,
  faLanguage,
  faListCheck,
  faPaperPlane,
  faPhone,
  faRoad,
  faRoute,
  faRulerCombined,
  faShareNodes,
  faShieldHalved,
  faStopwatch,
  faTemperatureHalf,
  faTruck,
  faUserTie,
  faWeightHanging,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import { EMPTY, Observable, of, combineLatest } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap, startWith } from 'rxjs/operators';
import { AiBrokerChatService } from '../../ai-broker-chat/ai-broker-chat.service';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { TimelineModule } from 'primeng/timeline';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PanelModule } from 'primeng/panel';
import { AvatarModule } from 'primeng/avatar';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { LoadRequestService } from '../load-request.service';
import { LoadMatchService } from '../../matches/load-match.service';
import { LoadMatch } from '../../matches/load-match.model';
import { GoogleMapComponent } from '../../shared/google-map/google-map.component';
import { CompanyService } from '../../company/company.service';
import { Company } from '../../company/company.model';
import { LoadRequest } from '../load-request.model';
import { LanguageService } from '../../services/language.service';
import { CurrencyResolverService } from '../../services/currency-resolver.service';

interface SummaryMetric {
  label: string;
  value: string;
  hint?: string | null;
}

interface DetailItem {
  label: string;
  value: string;
}

interface ViewModel {
  load: LoadRequest;
  company: Company | null;
  summaryMetrics: SummaryMetric[];
  equipmentMetrics: SummaryMetric[];
  generalDetails: DetailItem[];
  complianceDetails: DetailItem[];
  contactDetails: DetailItem[];
  requirementTags: string[];
  viaTags: string[];
  notes: string[];
  missingFields: string[];
  redFlags: string[];
  paymentTermsDisplay: string;
}

@Component({
  selector: 'app-load-details',
  standalone: true,
  templateUrl: './load-details.component.html',
  styleUrls: ['./load-details.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DividerModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    TagModule,
    SkeletonModule,
    TimelineModule,
    IftaLabelModule,
    PanelModule,
    GoogleMapComponent,
    TranslateModule,
    FontAwesomeModule,
    AvatarModule,
    RouterLink,
    ToastModule,
    TooltipModule
  ],
  providers: [CompanyService]
})
export class LoadDetailsComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  private readonly loadRequestService = inject(LoadRequestService);
  private readonly companyService = inject(CompanyService);
  private readonly loadMatchService = inject(LoadMatchService);
  private readonly messageService = inject(MessageService);
  private readonly aiChatService = inject(AiBrokerChatService);
  private readonly languageService = inject(LanguageService);
  private readonly currencyResolver = inject(CurrencyResolverService);

  readonly icons = {
    bolt: faBolt,
    box: faBox,
    boxOpen: faBoxOpen,
    back: faArrowLeft,
    building: faBuilding,
    calendar: faCalendarDay,
    checkCircle: faCheckCircle,
    circleInfo: faCircleInfo,
    clock: faClock,
    copy: faCopy,
    creditCard: faCreditCard,
    dollar: faDollarSign,
    eye: faEye,
    idBadge: faIdBadge,
    invoice: faFileInvoiceDollar,
    inbox: faInbox,
    language: faLanguage,
    listCheck: faListCheck,
    send: faPaperPlane,
    phone: faPhone,
    road: faRoad,
    route: faRoute,
    ruler: faRulerCombined,
    share: faShareNodes,
    shield: faShieldHalved,
    stopwatch: faStopwatch,
    temperature: faTemperatureHalf,
    truck: faTruck,
    userTie: faUserTie,
    weight: faWeightHanging,
    whatsapp: faWhatsapp
  };

  private readonly navigationStateLoad =
    (this.router.getCurrentNavigation()?.extras?.state?.['load'] as LoadRequest | undefined) ?? null;

  private readonly defaultValue = 'COMMON.NA';
  private readonly rateNotGivenValue = 'Not Given';
  private counterPrefilled = false;
  private currentLoad: LoadRequest | null = null;
  private readonly recordedViewIds = new Set<number>();

  errorMessage: string | null = null;
  mapMetrics = signal<{ distance: string; duration: string } | null>(null);

  iconForSummaryMetric(label: string): IconDefinition {
    const iconsByLabel: Record<string, IconDefinition> = {
      'COMMON.PICKUP': this.icons.calendar,
      'COMMON.DELIVERY': this.icons.calendar,
      'COMMON.DURATION': this.icons.clock,
      'LOADS.POST.DISTANCE': this.icons.road,
      'LOADS.POST.COMMODITY': this.icons.box
    };
    return iconsByLabel[label] ?? this.icons.circleInfo;
  }

  iconForEquipmentMetric(label: string): IconDefinition {
    if (label.includes('EQUIPMENT') || label === 'Equipment Needed' || label.includes('معدات')) {
      return this.icons.truck;
    }
    if (label.includes('LENGTH') || label.includes('LINEAR_FEET') || label === 'Volume' || label.includes('طول') || label.includes('حجم')) {
      return this.icons.ruler;
    }
    if (label === 'Weight' || label.includes('وزن')) {
      return this.icons.weight;
    }
    if (label.includes('PALLETS') || label.includes('QUANTITY') || label.includes('منصات') || label.includes('كمية')) {
      return this.icons.boxOpen;
    }
    if (label === 'Temperature' || label.includes('حرارة')) {
      return this.icons.temperature;
    }
    if (label === 'LOADS.POST.ROUTE_INFO') {
      return this.icons.route;
    }
    return this.icons.circleInfo;
  }

  iconForDetailRow(label: string): IconDefinition {
    if (label.includes('REFERENCE')) {
      return this.icons.idBadge;
    }
    if (label.includes('STATUS')) {
      return this.icons.checkCircle;
    }
    if (label === 'Shipper' || label === 'Client Company' || label.includes('شاحن') || label.includes('شركة')) {
      return this.icons.building;
    }
    if (label.includes('Window') || label.includes('Date') || label === 'Created' || label === 'Updated' || label.includes('نافذة') || label.includes('تاريخ') || label.includes('تحديث')) {
      return this.icons.calendar;
    }
    if (label === 'Currency' || label.includes('عملة')) {
      return this.icons.dollar;
    }
    if (label.includes('Insurance') || label.includes('تأمين')) {
      return this.icons.shield;
    }
    if (label.includes('Language') || label.includes('لغة')) {
      return this.icons.language;
    }
    if (label.includes('Payment') || label.includes('دفع')) {
      return this.icons.creditCard;
    }
    return this.icons.listCheck;
  }

  readonly counterForm: FormGroup = this.fb.group({
    counterAmount: [null, [Validators.required, Validators.min(0.01)]]
  });

  readonly vm$: Observable<ViewModel> = combineLatest([
    this.route.paramMap,
    this.aiChatService.refreshCanvas$.pipe(startWith(null)),
    toObservable(this.languageService.currentLanguage),
    toObservable(this.mapMetrics).pipe(startWith(null))
  ]).pipe(
    map(([params, _, lang, mapInfo]) => ({ params, lang, mapInfo })),
    switchMap(({ params, lang, mapInfo }) => {
      const idParam = params.get('id');
      const id = idParam ? Number(idParam) : NaN;
      let load$: Observable<LoadRequest>;

      if (!Number.isNaN(id) && id > 0) {
        load$ = this.loadRequestService.get(id, lang).pipe(
          catchError(error => {
            console.error('Failed to load request', error);
            this.errorMessage = 'LOADS.DETAILS.ERROR_LOADING';
            return EMPTY;
          })
        );
      } else if (idParam && this.isReferenceToken(idParam, 'REQ')) {
        load$ = this.loadRequestService.getByReferenceToken(idParam, lang).pipe(
          catchError(error => {
            console.error('Failed to load request by reference', error);
            this.errorMessage = 'LOADS.DETAILS.ERROR_LOADING';
            return EMPTY;
          })
        );
      } else if (this.navigationStateLoad) {
        load$ = of(this.navigationStateLoad);
      } else {
        this.errorMessage = 'Load information is not available.';
        return EMPTY;
      }

      return load$.pipe(
        switchMap(load => {
          const company$ = load.shipperCompanyId
            ? this.companyService.getCompanyById(load.shipperCompanyId).pipe(
              map(res => res.data),
              catchError(() => of(null))
            )
            : of(null);

          const matches$ = this.loadMatchService.search({ loadRequestId: load.id }, 0, 100).pipe(
            map((res) => res.content),
            catchError(() => of([]))
          );

          return combineLatest([company$, matches$]).pipe(
            map(([company, fullMatches]: [Company | null, LoadMatch[]]) => {
              // Calculate average proposed rate from matches if load.targetRate is null or 0
              let effectiveRate = load.targetRate ?? load.proposedRate;
              if ((!effectiveRate || effectiveRate === 0) && fullMatches.length > 0) {
                const rates = fullMatches.map(m => m.proposedRate).filter((r): r is number => r != null && r > 0);
                if (rates.length > 0) {
                  effectiveRate = rates.reduce((a, b) => a + b, 0) / rates.length;
                }
              }

              // Enhance load with matches data if needed
              const enhancedLoad: LoadRequest = {
                ...load,
                proposedRate: effectiveRate ?? load.proposedRate
              };

              return { load: enhancedLoad, mapInfo, company };
            })
          );
        })
      );
    }),
    tap(({ load }) => {
      this.errorMessage = null;
      this.currentLoad = load;
      this.recordViewOnce(load.id);
      if (!this.counterPrefilled && load.targetRate != null && load.targetRate > 0) {
        this.counterForm.patchValue({ counterAmount: load.targetRate }, { emitEvent: false });
        this.counterPrefilled = true;
      }
    }),
    map(({ load, mapInfo, company }) => this.buildViewModel(load, mapInfo, company)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  counterSubmitted = false;
  hasAccepted = false;

  submitCounter(): void {
    if (this.counterForm.invalid) {
      this.counterForm.markAllAsTouched();
      return;
    }

    this.counterSubmitted = true;
    this.hasAccepted = false;

    if (!this.currentLoad?.id) {
      this.messageService.add({
        severity: 'warn',
        summary: this.ui('Load unavailable', 'الشحنة غير متاحة'),
        detail: this.ui('Load information is not available.', 'معلومات الشحنة غير متاحة.')
      });
      return;
    }

    const offeredRate = Number(this.counterForm.value.counterAmount);
    if (!Number.isFinite(offeredRate) || offeredRate <= 0) {
      this.counterForm.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: this.ui('Offer required', 'العرض مطلوب'),
        detail: this.ui('Enter a valid offer amount greater than zero.', 'أدخل قيمة عرض صالحة أكبر من صفر.')
      });
      return;
    }

    window.sessionStorage.setItem(this.offerStorageKey(this.currentLoad.id), JSON.stringify({
      loadRequestId: this.currentLoad.id,
      offeredRate,
      currencyCode: this.effectiveCurrencyCode(this.currentLoad.currencyCode),
      timestamp: Date.now()
    }));

    this.router.navigate(['/load-details', this.currentLoad.id, 'find-availabilities']);
  }

  onMapInfoReceived(info: { distance: string; duration: string }): void {
    this.mapMetrics.set(info);
  }

  acceptLoad(): void {
    if (!this.currentLoad) return;
    this.router.navigate(['/load-details', this.currentLoad.id, 'find-availabilities']);
  }

  goBack(fallbackRoute: string): void {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    void this.router.navigate([fallbackRoute]);
  }

  requestChanges(): void {
    console.debug('Change requested for load', {
      loadId: this.currentLoad?.id ?? 'unknown',
      reference: this.currentLoad?.referenceNumber ?? 'N/A'
    });
  }


  shareToWhatsApp(): void {
    if (!this.currentLoad) return;

    const load = this.currentLoad;
    const phone = this.normalizedContactPhone(load.contactNumber);
    if (!phone) {
      this.messageService.add({
        severity: 'warn',
        summary: this.ui('Phone unavailable', 'رقم الهاتف غير متاح'),
        detail: this.ui('No contact phone number is available for this load.', 'لا يوجد رقم هاتف تواصل متاح لهذه الشحنة.'),
      });
      return;
    }

    const rateText = this.formatRate(load.targetRate ?? load.proposedRate, load.currencyCode);
    const publicUrl = this.publicLoadUrl(load);

    // Using simplified structure for better display in WhatsApp
    const message = [
      `*Load Details: ${load.referenceNumber}*`,
      `Origin: ${load.origin}`,
      `Destination: ${load.destination}`,
      `Equipment: ${load.equipmentType || 'N/A'}`,
      `Weight: ${this.formatWeight(load.weightLbs, load.weightKg)}`,
      `Pickup: ${this.formatDate(load.pickupDate)}`,
      `Target Rate: ${rateText}`,
      '',
      `*Source:* LoadGate public load details`,
      publicUrl
    ].join('\n');

    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  }

  callContact(): void {
    const phone = this.normalizedContactPhone(this.currentLoad?.contactNumber);
    if (!phone) {
      this.messageService.add({
        severity: 'warn',
        summary: this.ui('Phone unavailable', 'رقم الهاتف غير متاح'),
        detail: this.ui('No contact phone number is available for this load.', 'لا يوجد رقم هاتف تواصل متاح لهذه الشحنة.'),
      });
      return;
    }

    window.location.href = `tel:${phone}`;
  }

  async shareLoad(): Promise<void> {
    if (!this.currentLoad) return;

    const shareData = {
      title: `LoadGate: ${this.currentLoad.referenceNumber}`,
      text: `Load request from ${this.currentLoad.origin} to ${this.currentLoad.destination}. Equipment: ${this.currentLoad.equipmentType || 'N/A'}`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        this.messageService.add({
          severity: 'success',
          summary: this.ui('Copied', 'تم النسخ'),
          detail: this.ui('Link copied to clipboard', 'تم نسخ الرابط إلى الحافظة'),
        });
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        this.messageService.add({
          severity: 'error',
          summary: this.ui('Error', 'خطأ'),
          detail: this.ui('Could not share the load', 'تعذر مشاركة الشحنة'),
        });
      }
    }
  }

  copyDetails(): void {
    if (!this.currentLoad) return;

    const load = this.currentLoad;
    const rate = load.targetRate ?? load.proposedRate;
    const text = `
Load Details: ${load.referenceNumber}
Origin: ${load.origin}
Destination: ${load.destination}
Equipment: ${load.equipmentType || 'N/A'}
Weight: ${this.formatWeight(load.weightLbs, load.weightKg)}
Pickup: ${this.formatDate(load.pickupDate)}
Target Rate: ${this.formatRate(rate, load.currencyCode)}

View more: ${window.location.href}
    `.trim();

    navigator.clipboard.writeText(text).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.ui('Copied', 'تم النسخ'),
        detail: this.ui('Full details copied to clipboard', 'تم نسخ التفاصيل الكاملة إلى الحافظة'),
      });
    });
  }

  protected signalBadges(load: LoadRequest): Array<{ icon: IconDefinition; label: string; strong?: boolean }> {
    const signals = load.signals;
    const badges: Array<{ icon: IconDefinition; label: string; strong?: boolean }> = [
      { icon: this.icons.clock, label: this.ui(`Posted ${this.formatRelativeTime(load.createdAt)}`, `نشر ${this.formatRelativeTime(load.createdAt)}`) }
    ];
    if (!signals) {
      return badges;
    }
    if ((signals.viewCount || 0) > 0) badges.push({ icon: this.icons.eye, label: this.ui(`Viewed ${signals.viewCount} times`, `شوهد ${signals.viewCount} مرة`) });
    if ((signals.offersSubmittedCount || 0) > 0) badges.push({ icon: this.icons.send, label: this.ui(`${signals.offersSubmittedCount} offers`, `${signals.offersSubmittedCount} عروض`) });
    if ((signals.interestedUsersCount || 0) > 0) badges.push({ icon: this.icons.userTie, label: this.ui(`${signals.interestedUsersCount} interested`, `${signals.interestedUsersCount} مهتم`) });
    if (signals.hotLane) badges.push({ icon: this.icons.bolt, label: this.ui('Hot lane', 'مسار نشط'), strong: true });
    if (signals.fastPickup) badges.push({ icon: this.icons.stopwatch, label: this.ui('Fast pickup', 'استلام سريع'), strong: true });
    if (signals.verifiedShipper) badges.push({ icon: this.icons.shield, label: this.ui('Verified shipper', 'شاحن موثق'), strong: true });
    return badges;
  }

  ui(en: string, ar: string): string {
    return this.isArabic() ? ar : en;
  }

  isArabic(): boolean {
    return this.languageService.currentLanguage() === 'ar';
  }

  effectiveCurrencyCode(code?: string | null): string {
    return this.currencyResolver.effectiveCurrencyCode(code);
  }

  routeTitle(origin?: string | null, destination?: string | null): string {
    const from = origin || this.ui('Origin', 'الاستلام');
    const to = destination || this.ui('Destination', 'التسليم');
    return this.isArabic() ? `${from} إلى ${to}` : `${from} to ${to}`;
  }

  statusLabel(value?: string | null): string {
    const key = this.normalizeEnumKey(value);
    const ar: Record<string, string> = {
      ACTIVE: 'نشط',
      POSTED: 'منشور',
      BOOKED: 'محجوز',
      CANCELLED: 'ملغي',
      EXPIRED: 'منتهي'
    };
    return this.isArabic() ? (ar[key] ?? this.labelize(value)) : (value || 'POSTED');
  }

  displayDate(value?: string | null, includeWeekday = false): string {
    if (!value) {
      return this.ui('N/A', 'غير متوفر');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString(this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined, {
      weekday: includeWeekday ? 'short' : undefined,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  protected labelize(value?: string | null): string {
    if (!value) {
      return '';
    }
    if (this.isArabic()) {
      const ar = this.arabicEnumLabels()[this.normalizeEnumKey(value)];
      if (ar) {
        return ar;
      }
    }
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  protected formatTrustBoolean(value: boolean | null | undefined): string {
    if (value == null) {
      return this.ui('N/A', 'غير متوفر');
    }
    return value ? this.ui('Yes', 'نعم') : this.ui('No', 'لا');
  }

  private recordViewOnce(id?: number | null): void {
    if (!id || this.recordedViewIds.has(id)) {
      return;
    }
    this.recordedViewIds.add(id);
    this.loadRequestService.recordView(id).subscribe({ error: () => undefined });
  }

  private formatRelativeTime(value?: string | null): string {
    if (!value) {
      return this.ui('recently', 'مؤخراً');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.ui('recently', 'مؤخراً');
    }
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return this.ui('just now', 'الآن');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return this.ui(`${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`, `قبل ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`);
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.ui(`${hours} ${hours === 1 ? 'hour' : 'hours'} ago`, `قبل ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`);
    const days = Math.floor(hours / 24);
    if (days < 30) return this.ui(`${days} ${days === 1 ? 'day' : 'days'} ago`, `قبل ${days} ${days === 1 ? 'يوم' : 'أيام'}`);
    const months = Math.floor(days / 30);
    if (months < 12) return this.ui(`${months} ${months === 1 ? 'month' : 'months'} ago`, `قبل ${months} ${months === 1 ? 'شهر' : 'أشهر'}`);
    const years = Math.floor(months / 12);
    return this.ui(`${years} ${years === 1 ? 'year' : 'years'} ago`, `قبل ${years} ${years === 1 ? 'سنة' : 'سنوات'}`);
  }


  private buildViewModel(load: LoadRequest, mapInfo: { distance: string; duration: string } | null = null, company: Company | null = null): ViewModel {
    const summaryMetrics = this.buildSummaryMetrics(load, mapInfo);

    return {
      load,
      company,
      summaryMetrics,
      equipmentMetrics: this.buildEquipmentMetrics(load),
      generalDetails: this.buildGeneralDetails(load),
      complianceDetails: this.buildComplianceDetails(load),
      contactDetails: this.buildContactDetails(load),
      requirementTags: this.buildRequirementTags(load),
      viaTags: load.via?.filter(Boolean) ?? [],
      notes: load.notes?.filter(Boolean) ?? [],
      missingFields: load.missingFields?.filter(Boolean) ?? [],
      redFlags: load.redFlags?.filter(Boolean) ?? [],
      paymentTermsDisplay: load.paymentTermsLabel?.trim().length
        ? load.paymentTermsLabel
        : (load.paymentTerms?.trim().length ? load.paymentTerms : this.defaultValue)
    };
  }

  private buildSummaryMetrics(load: LoadRequest, mapInfo: { distance: string; duration: string } | null = null): SummaryMetric[] {
    return [
      {
        label: 'COMMON.PICKUP',
        value: this.formatDate(load.pickupDate),
        hint: this.buildWindowHint(load.pickupWindowStart, load.pickupWindowEnd)
      },
      {
        label: 'COMMON.DELIVERY',
        value: this.formatDate(load.deliveryDate)
      },
      {
        label: 'COMMON.DURATION',
        value: mapInfo?.duration ?? this.defaultValue
      },
      {
        label: 'LOADS.POST.DISTANCE',
        value: mapInfo?.distance ?? this.formatDistance(load.totalDistanceInKm)
      },
      {
        label: 'LOADS.POST.COMMODITY',
        value: load.commodityLabel?.trim().length
          ? load.commodityLabel
          : (load.commodity?.trim().length ? load.commodity : this.defaultValue)
      }
    ];
  }

  private buildEquipmentMetrics(load: LoadRequest): SummaryMetric[] {
    const metrics: SummaryMetric[] = [];

    metrics.push({
      label: 'COMMON.EQUIPMENT',
      value: load.equipmentTypeLabel?.trim().length
        ? load.equipmentTypeLabel
        : (load.equipmentType?.trim().length ? load.equipmentType : this.defaultValue)
    });

    if (load.vehicleType?.length) {
      metrics.push({
        label: 'AVAILABILITIES.POST.EQUIPMENT_TYPE',
        value: load.vehicleType.join(', ')
      });
    }

    if (load.vehicleConfig?.length) {
      metrics.push({
        label: 'LOADS.POST.ROUTE_INFO',
        value: load.vehicleConfig.join(', ')
      });
    }

    if (load.vehicleLength != null) {
      metrics.push({
        label: 'COMMON.LENGTH',
        value: `${load.vehicleLength} ft`
      });
    }

    if (load.vehicleWidth != null) {
      metrics.push({
        label: 'AVAILABILITIES.POST.LINEAR_FEET',
        value: `${load.vehicleWidth} ft`
      });
    }

    if (load.vehicleHeight != null) {
      metrics.push({
        label: 'AVAILABILITIES.POST.LINEAR_FEET',
        value: `${load.vehicleHeight} ft`
      });
    }

    if (load.vehicleVolume != null) {
      metrics.push({
        label: 'Volume',
        value: `${load.vehicleVolume} cu ft`
      });
    }

    if (load.weightLbs != null || load.weightKg != null) {
      metrics.push({
        label: 'Weight',
        value: this.formatWeight(load.weightLbs, load.weightKg)
      });
    }

    if (load.pallets != null) {
      metrics.push({
        label: 'COMMON.PALLETS',
        value: `${load.pallets}`
      });
    }

    if (load.equipmentCount != null) {
      metrics.push({
        label: 'Equipment Needed',
        value: `${load.equipmentCount}`
      });
    }

    if (load.quantity?.trim().length) {
      metrics.push({
        label: 'COMMON.QUANTITY',
        value: load.quantity
      });
    }

    if (load.temperatureControlled) {
      metrics.push({
        label: 'Temperature',
        value: 'Temperature Controlled'
      });
    }

    return metrics;
  }

  private buildGeneralDetails(load: LoadRequest): DetailItem[] {
    return [
      {
        label: 'Shipper',
        value: load.clientCompanyName?.trim().length
          ? load.clientCompanyName
          : (load.shipperCompanyId ? this.ui('Registered shipper', 'شاحن مسجل') : this.ui('B2C Load Request', 'طلب حمل B2C'))
      },
      {
        label: this.ui('Pickup Window', 'نافذة الاستلام'),
        value: this.buildWindowHint(load.pickupWindowStart, load.pickupWindowEnd) ?? this.defaultValue
      },
      {
        label: this.ui('Delivery Date', 'تاريخ التسليم'),
        value: this.formatDate(load.deliveryDate)
      },
      {
        label: this.ui('Currency', 'العملة'),
        value: load.currencyCode?.trim().length ? load.currencyCode : this.defaultValue
      },
      {
        label: this.ui('Updated', 'آخر تحديث'),
        value: this.formatDate(load.updatedAt, true)
      }
    ];
  }

  private buildComplianceDetails(load: LoadRequest): DetailItem[] {
    return [
      { label: this.ui('Insurance Required', 'التأمين مطلوب'), value: this.formatBoolean(load.insuranceRequired) },
      {
        label: this.ui('Source Language', 'لغة المصدر'),
        value: load.sourceLanguage?.trim().length ? load.sourceLanguage : this.defaultValue
      }
    ];
  }

  private buildContactDetails(load: LoadRequest): DetailItem[] {
    return [
      {
        label: 'COMMON.CONTACT',
        value: this.composeContact(load.contactName, load.contactNumber)
      },
      {
        label: this.ui('Client Company', 'شركة العميل'),
        value: load.clientCompanyName?.trim().length ? load.clientCompanyName : this.defaultValue
      }
    ];
  }

  private buildRequirementTags(load: LoadRequest): string[] {
    const tags: string[] = [];

    if (load.equipmentType?.trim().length) {
      tags.push(load.equipmentTypeLabel?.trim().length ? load.equipmentTypeLabel : load.equipmentType);
    }

    if (load.vehicleType?.length) {
      tags.push(...load.vehicleType);
    }

    if (load.vehicleConfig?.length) {
      tags.push(...load.vehicleConfig);
    }

    if (load.temperatureControlled) {
      tags.push(this.ui('Temp Controlled', 'تحكم بالحرارة'));
    }

    return Array.from(new Set(tags));
  }

  private buildWindowHint(start?: string | null, end?: string | null): string | null {
    if (!start && !end) {
      return null;
    }

    const startText = this.formatTime(start);
    const endText = this.formatTime(end);

    if (startText && endText) {
      return `${startText} - ${endText}`;
    }

    return startText ?? endText ?? null;
  }

  private formatDate(value: string | null | undefined, includeTime = false): string {
    if (!value) {
      return this.defaultValue;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const options: Intl.DateTimeFormatOptions = includeTime
      ? { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { month: 'short', day: 'numeric', year: 'numeric' };

    return date.toLocaleString(this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined, options);
  }

  private formatTime(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString(this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined, { hour: '2-digit', minute: '2-digit' });
    }

    return value;
  }

  private formatCurrency(amount: number | null | undefined, code?: string | null): string {
    if (amount == null) {
      return this.defaultValue;
    }

    return this.currencyResolver.formatCurrency(amount, {
      currencyCode: code,
      locale: this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined,
      maximumFractionDigits: 2
    });
  }

  formatRate(amount: number | null | undefined, code?: string | null): string {
    if (amount == null || amount <= 0) {
      return this.ui(this.rateNotGivenValue, 'غير متوفر');
    }
    return this.formatCurrency(amount, code);
  }

  private offerStorageKey(loadId: number): string {
    return `load-offer:${loadId}`;
  }

  private isReferenceToken(value: string, prefix: string): boolean {
    return new RegExp(`^${prefix}-(?:\\d{4}-\\d{2}-)?\\d+$`, 'i').test(value);
  }

  private publicLoadUrl(load: LoadRequest): string {
    const id = load.id;
    if (!id) {
      return window.location.href;
    }

    return `${window.location.origin}/public/loads/${id}`;
  }

  private normalizedContactPhone(value?: string | null): string | null {
    const phone = value?.replace(/\D/g, '') ?? '';
    return phone.length ? phone : null;
  }

  private formatDistance(distanceKm: number | null | undefined): string {
    if (distanceKm == null) {
      return this.defaultValue;
    }

    const miles = distanceKm * 0.621371;
    return this.isArabic()
      ? `${distanceKm.toFixed(0)} كم (${miles.toFixed(0)} ميل)`
      : `${distanceKm.toFixed(0)} km (${miles.toFixed(0)} mi)`;
  }

  private formatWeight(weightLbs: number | null | undefined, weightKg: number | null | undefined): string {
    if (weightLbs == null && weightKg == null) {
      return this.defaultValue;
    }

    const parts: string[] = [];

    if (weightLbs != null) {
      parts.push(this.isArabic() ? `${weightLbs.toLocaleString()} رطل` : `${weightLbs.toLocaleString()} lb`);
    }

    if (weightKg != null) {
      parts.push(this.isArabic() ? `${weightKg.toLocaleString()} كجم` : `${weightKg.toLocaleString()} kg`);
    }

    return parts.join(' / ');
  }

  private formatNumber(value: number | null | undefined): string {
    if (value == null) {
      return this.defaultValue;
    }

    return value.toString();
  }

  private formatBoolean(value: boolean | null | undefined): string {
    if (value == null) {
      return this.defaultValue;
    }

    return value ? 'COMMON.YES' : 'COMMON.NO';
  }

  private composeContact(name?: string | null, phone?: string | null): string {
    const parts = [name?.trim(), phone?.trim()].filter(part => part && part.length);
    return parts.length ? parts.join(' | ') : this.defaultValue;
  }

  private normalizeEnumKey(value?: string | null): string {
    return (value ?? '')
      .trim()
      .replace(/[-\s]+/g, '_')
      .replace(/:+/g, '')
      .toUpperCase();
  }

  private arabicEnumLabels(): Record<string, string> {
    return {
      UNVERIFIED: 'غير موثق',
      VERIFIED: 'موثق',
      PENDING: 'قيد المراجعة',
      LIMITED_HISTORY: 'سجل محدود',
      COMMUNICATION_ONLY: 'تواصل فقط',
      PAYMENT_LIMITED_HISTORY: 'الدفع: سجل محدود',
      NEW_UNVERIFIED_PROFILE: 'ملف جديد/غير موثق',
      MARKETPLACE_LEAD: 'عميل من السوق',
      TRUST_PROFILE_NOT_AVAILABLE: 'ملف الثقة غير متاح',
      REVIEWED_TRUST_PROFILE: 'ملف ثقة تمت مراجعته',
      COMPANY_PROFILE: 'ملف الشركة',
      CONTACT_PERSON: 'جهة اتصال',
      INDIVIDUAL: 'فرد',
      BUSINESS: 'شركة',
      ACTIVE: 'نشط',
      POSTED: 'منشور',
      BOOKED: 'محجوز'
    };
  }
}
